/**
 * Career — banked Corporate Experience and what it buys (task 12.7).
 *
 * The perks themselves are content and their effects are the engine's; this is
 * the shop. Two rules shape the whole screen, and both are the spec's:
 *
 *   - a perk you cannot afford is shown *locked with its cost and the
 *     shortfall*, never hidden. What you are working towards is the point of
 *     working towards it;
 *   - owned perks go into a limited number of slots, so the screen requires a
 *     choice rather than accumulating every effect. Equipping into a full set
 *     is refused with the reason attached, not silently ignored.
 *
 * Equipping takes effect at the *next* mission start, which the screen says
 * plainly: a perk is folded into the run's settings at `createGame`, and a run
 * is scored against what it was opened with.
 */
import type { JSX } from 'react';
import { AsyncBoundary } from '../components/AsyncBoundary.js';
import { Button } from '../components/Button.js';
import { Card, CardBody } from '../components/Card.js';
import { LaunchLayout, PageHeading } from '../components/LaunchLayout.js';
import { careerView, type PerkRow } from '../lib/career.js';
import { useContentSource } from '../lib/content-context.js';
import { EMPTY_CAREER } from '../lib/save/types.js';
import { useAsync } from '../hooks/use-async.js';
import { useActiveProfile, useViewStore } from '../store/view-store.js';

function Stat({
  label,
  value,
  testId,
}: {
  readonly label: string;
  readonly value: string;
  readonly testId: string;
}): JSX.Element {
  return (
    <div className="rounded-lg bg-surface-muted px-3 py-2">
      <dt className="text-xs text-ink-subtle">{label}</dt>
      <dd className="mt-0.5 font-semibold text-ink" data-testid={testId}>
        {value}
      </dd>
    </div>
  );
}

function PerkCard({
  row,
  equipSlots,
  onBuy,
  onEquip,
}: {
  readonly row: PerkRow;
  readonly equipSlots: number;
  onBuy(row: PerkRow): void;
  onEquip(row: PerkRow, equipped: boolean): void;
}): JSX.Element {
  return (
    <Card data-testid={`perk-${row.id}`} data-owned={row.owned ? 'true' : 'false'}>
      <CardBody>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-ink">{row.label}</h3>
            <p className="text-xs text-ink-subtle" data-testid={`perk-cost-${row.id}`}>
              {row.owned ? 'Owned' : `${row.cost.toLocaleString()} CXP`}
            </p>
          </div>
          {row.equipped ? (
            <span
              className="rounded border border-accent px-2 py-0.5 text-xs font-medium text-accent"
              data-testid={`perk-equipped-${row.id}`}
            >
              Equipped
            </span>
          ) : null}
        </div>

        <p className="mt-2 text-sm text-ink">{row.description}</p>

        {!row.owned && !row.affordable ? (
          <p className="mt-2 text-xs text-warning" data-testid={`perk-shortfall-${row.id}`}>
            {`This perk is locked. You need ${row.shortfall.toLocaleString()} more CXP to buy it.`}
          </p>
        ) : null}

        {row.blockedByEquipLimit ? (
          <p className="mt-2 text-xs text-ink-muted" data-testid={`perk-blocked-${row.id}`}>
            {`All ${String(equipSlots)} perk slots are full. Unequip a perk to make room for this one.`}
          </p>
        ) : null}

        <div className="mt-3 flex flex-wrap gap-2">
          {row.owned ? (
            <Button
              variant={row.equipped ? 'secondary' : 'primary'}
              disabled={row.blockedByEquipLimit}
              data-testid={`perk-equip-${row.id}`}
              onClick={() => {
                onEquip(row, !row.equipped);
              }}
            >
              {row.equipped ? 'Unequip' : 'Equip'}
            </Button>
          ) : (
            <Button
              variant="primary"
              disabled={!row.affordable}
              data-testid={`perk-buy-${row.id}`}
              onClick={() => {
                onBuy(row);
              }}
            >
              {`Buy for ${row.cost.toLocaleString()} CXP`}
            </Button>
          )}
        </div>
      </CardBody>
    </Card>
  );
}

export function CareerScreen(): JSX.Element {
  const source = useContentSource();
  const profile = useActiveProfile();
  const buyPerk = useViewStore((state) => state.buyPerk);
  const setPerkEquipped = useViewStore((state) => state.setPerkEquipped);
  const announce = useViewStore((state) => state.announce);
  const catalogue = useAsync(() => source.perks(), [source]);

  const career = profile?.career ?? EMPTY_CAREER;

  return (
    <LaunchLayout title="Career">
      <PageHeading
        title="Career"
        lede="You earn Corporate Experience (CXP) at each Quarterly Review. Spend it in Career on perks, which are permanent upgrades that apply to every future run."
      />

      <AsyncBoundary result={catalogue} label="the perk catalog">
        {(perks) => {
          const view = careerView(perks, career);
          return (
            <>
              <Card>
                <CardBody>
                  <p className="text-sm text-ink-subtle">Banked Corporate Experience</p>
                  <p className="mt-1 text-3xl font-bold text-ink" data-testid="career-cxp">
                    {`${view.cxp.toLocaleString()} CXP`}
                  </p>
                  <dl className="mt-5 grid grid-cols-2 gap-3 text-sm">
                    <Stat
                      label="Perks owned"
                      value={String(career.ownedPerkIds.length)}
                      testId="career-owned"
                    />
                    <Stat
                      label="Slots used"
                      value={`${String(view.equippedCount)} of ${String(view.equipSlots)}`}
                      testId="career-equipped"
                    />
                  </dl>
                  <p className="mt-3 text-xs text-ink-muted" data-testid="career-slots-note">
                    {`You can carry ${String(view.equipSlots)} perk${view.equipSlots === 1 ? '' : 's'} at a time. A mission opens with whichever perks you have equipped then. The run is played and scored with those, even if you change them later.`}
                  </p>
                </CardBody>
              </Card>

              <h2 className="mt-8 mb-3 text-lg font-semibold text-ink">Perks</h2>
              <ul className="flex list-none flex-col gap-4 p-0" data-testid="perk-catalogue">
                {view.rows.map((row) => (
                  <li key={row.id}>
                    <PerkCard
                      row={row}
                      equipSlots={view.equipSlots}
                      onBuy={(bought) => {
                        buyPerk(bought.id, bought.cost);
                        announce(`${bought.label} bought.`);
                      }}
                      onEquip={(target, equipped) => {
                        setPerkEquipped(target.id, equipped, view.equipSlots);
                        announce(`${target.label} ${equipped ? 'equipped' : 'unequipped'}.`);
                      }}
                    />
                  </li>
                ))}
              </ul>
            </>
          );
        }}
      </AsyncBoundary>
    </LaunchLayout>
  );
}
